import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { PropertiesService } from '../properties.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Category,
  FileType,
  InvestementStatus,
  InvestmentStrategyType,
  Property,
  PropertyViewPageConfiguration,
  RentalType,
  State,
} from '../properties.model';
import { Subscription } from 'rxjs';
import { NgProgressService } from '@shared/services/ng-progress.service';
import { SkeletonModule } from 'primeng/skeleton';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ProgressBarModule } from 'primeng/progressbar';
import { PopoverModule } from 'primeng/popover';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { FluidModule } from 'primeng/fluid';
import { ButtonModule } from 'primeng/button';
import {
  Form,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CartService } from '../../cart/cart.service';
import { DialogModule } from 'primeng/dialog';
import { HttpClient } from '@angular/common/http';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { GoogleMapsService } from '@shared/services/google-maps.service';
import { TablistComponent } from '@shared/components/tablist/tablist.component';
import { TimelineComponent } from '@shared/components/timeline/timeline.component';
import { InvestmentCalculatorComponent } from '@shared/components/investment-calculator/investment-calculator.component';
import { DocumentsService } from '@shared/services/documents.service';
import { MenuItem } from 'primeng/api';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CustomCurrencyPipe } from '@shared/pipes/custom-currency.pipe';
import { ProfileService } from '../../profile/profile.service';
import { MessageModule } from 'primeng/message';
import { ChatbotService } from '@shared/components/chatbot/chatbot.service';
import {
  DialogService,
  DynamicDialogModule,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { ContentViewerComponent } from '@shared/components/content-viewer/content-viewer.component';
import { AuthService } from '@shared/services/auth.service';
import { CarouselModule } from 'primeng/carousel';

@Component({
  selector: 'app-property-view',
  imports: [
    CommonModule,
    SkeletonModule,
    CardModule,
    DividerModule,
    AngularSvgIconModule,
    ProgressBarModule,
    PopoverModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    ButtonModule,
    FluidModule,
    ReactiveFormsModule,
    DialogModule,
    GoogleMap,
    MapMarker,
    TablistComponent,
    TimelineComponent,
    FormsModule,
    InvestmentCalculatorComponent,
    BreadcrumbComponent,
    CustomCurrencyPipe,
    MessageModule,
    DynamicDialogModule,
    CarouselModule,
  ],
  templateUrl: './property-view.component.html',
  providers: [DialogService],
})
export class PropertyViewComponent implements OnInit, AfterViewInit, OnDestroy {
  property: Property | null = null;
  propertyViewPageConfiguration: PropertyViewPageConfiguration | undefined;
  isAnonymousSession: boolean = false;
  currency: string | undefined;
  categoryEnum = Category;
  state = State;
  isDescriptionExpanded: boolean = false;
  showOwnershipDocumentsDialog: boolean = false;
  markerPosition: google.maps.LatLngLiteral = { lat: 20, lng: 53 };
  markerTitle: string = '';
  googleMapsUrl: string = '';
  activeFinancialsTab: number = FinancialsTab.PROPERTY_COST;
  FinancialsTab = FinancialsTab;
  InvestementStatus = InvestementStatus;
  RentalType = RentalType;
  State = State;
  contentViewerRef: DynamicDialogRef | undefined;
  isTouchDevice: boolean = false;

  breadcrumdItems: MenuItem[] = [];
  readonly zoom = 15;
  readonly InvestmentStrategyTypeEnum = InvestmentStrategyType;
  readonly form: FormGroup;
  readonly MINIMUM_AMOUNT = 100;
  readonly MAXIMUM_AMOUNT = 100000;
  readonly DESCIPRION_SHOW_LESS_LIMIT = 350;

  readonly mapOptions: google.maps.MapOptions = {
    // disableDefaultUI: true, // ✅ Hides all UI controls (zoom, map type, etc.)
    // zoomControl: true, // ✅ Disables zoom controls
    // scrollwheel: true, // ✅ Disables scroll wheel zoom
    // disableDoubleClickZoom: true, // ✅ Disables zoom on double click
    // draggable: true, // ✅ Disables dragging
    // gestureHandling: 'none', // ✅ Disables all touch gestures
    mapTypeControl: false, // ✅ Hides map type controls (e.g., satellite)
    streetViewControl: false, // ✅ Hides street view
    fullscreenControl: false, // ✅ Hides fullscreen button
    cameraControl: false,
    keyboardShortcuts: false,
    styles: [
      {
        elementType: 'geometry',
        stylers: [{ color: '#f5f5f5' }],
      },
      {
        elementType: 'labels.icon',
        stylers: [{ visibility: 'off' }],
      },
      {
        elementType: 'labels.text.fill',
        stylers: [{ color: '#616161' }],
      },
      {
        elementType: 'labels.text.stroke',
        stylers: [{ color: '#f5f5f5' }],
      },
      {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#c9c9c9' }],
      },
      {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#9e9e9e' }],
      },
      {
        featureType: 'landscape.natural',
        elementType: 'geometry.fill',
        stylers: [{ color: '#f5f5f5' }],
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ color: '#ffffff' }],
      },
      {
        featureType: 'road.arterial',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#757575' }],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [{ color: '#dadada' }],
      },
      {
        featureType: 'road.highway',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#616161' }],
      },
      {
        featureType: 'road.local',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#9e9e9e' }],
      },
      {
        featureType: 'transit.line',
        elementType: 'geometry',
        stylers: [{ color: '#e5e5e5' }],
      },
      {
        featureType: 'transit.station',
        elementType: 'geometry',
        stylers: [{ color: '#eeeeee' }],
      },
    ],
  };

  private propertySub = Subscription.EMPTY;
  private bookmarkedPropertiesSub = Subscription.EMPTY;
  private isAnonymousSessionSub = Subscription.EMPTY;

  private readonly _propertiesService = inject(PropertiesService);
  private readonly _cartService = inject(CartService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _ngProgressService = inject(NgProgressService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _httpClient = inject(HttpClient);
  private readonly _googleMapsService = inject(GoogleMapsService);
  private readonly _documentsService = inject(DocumentsService);
  private readonly _profileService = inject(ProfileService);
  private readonly _chatbotService = inject(ChatbotService);
  private readonly _dialogService = inject(DialogService);
  private readonly _authService = inject(AuthService);

  get f() {
    return this.form.controls;
  }

  constructor() {
    this.form = this._formBuilder.group({
      amount: [
        100,
        [
          Validators.required,
          Validators.min(this.MINIMUM_AMOUNT),
          Validators.max(this.MAXIMUM_AMOUNT),
        ],
      ],
    });
  }

  async ngOnInit(): Promise<void> {
    this.isTouchDevice = matchMedia('(pointer: coarse)').matches;
    this._ngProgressService.startLoader();
    this.currency = this._profileService.profile.currency;

    this._propertiesService.getBookmarkedProperties();

    const id = this._activatedRoute.snapshot.paramMap.get('id');
    this.propertySub = this._propertiesService.property$.subscribe((res) => {
      console.log('Property:', res);
      if (!res) {
        return;
      } else if (res.$id !== id) {
        this._propertiesService.getProperty(id!);
        return;
      }

      this.propertyViewPageConfiguration =
        this._propertiesService.getPropertyViewPageConfiguration(res.state);

      res.description = res.description?.replaceAll('\n', '<br>');
      res.isBookmarked = this._propertiesService.isPropertyBookmarked(res.$id);

      this.property = res;
      this._ngProgressService.completeLoader();

      this.initializeMap();
      this.setupBreadcrumb(res.name);
    });
    this._propertiesService.getProperty(id!);

    this.isAnonymousSessionSub =
      this._authService.isAnonymousSession$.subscribe((isAnonymousSession) => {
        this.isAnonymousSession = isAnonymousSession;
      });
  }

  ngAfterViewInit(): void {
    this.initializeMap();
  }

  onPropertyBookmark(): void {
    this.property!.isBookmarked = !this.property?.isBookmarked;
    this._propertiesService.bookmarkProperty(
      this.property!.$id,
      this.property!.isBookmarked
    );
  }

  onShowPhotosGallery(): void {
    this._router.navigate(['photos'], { relativeTo: this._activatedRoute });
  }

  onShowContentViewer(url: string): void {
    this.contentViewerRef = this._dialogService.open(ContentViewerComponent, {
      inputValues: {
        url: url,
      },
      width: '100%',
      height: '100%',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: false,
      closable: true,
      styleClass: 'content-viewer-dialog',
    });
  }

  onAddToCart(): void {
    if (this.form.invalid) {
      return;
    }

    this._cartService.addToCart(this.property!, this.f['amount'].value);
    this._router.navigate(['/home/cart']);
  }

  onAddAmount(amount: number): void {
    if (this.form.invalid) {
      return;
    }

    this.f['amount'].setValue(this.f['amount'].value + amount);
    this.form.markAllAsTouched();
  }

  onDownloadDocument(index: number, event: Event): void {
    event.preventDefault(); // Prevents navigation
    event.stopPropagation(); // Stops event from bubbling to <a>

    const documentUrl = this.property!.documents![index].url;
    const documentName = `${this.property!.documents![index].name}.${
      this.property!.documents![index].type
    }`;

    this._documentsService.onDownloadDocument(documentUrl, documentName);
  }

  openGoogleMaps(): void {
    window.open(this.googleMapsUrl, '_blank');
  }

  onSendMessage(): void {
    this._chatbotService.showChatbot();
  }

  private initializeMap(): void {
    this._googleMapsService.loadGoogleMaps().subscribe((loaded) => {
      if (loaded) {
        this.setMarker();
      } else {
        console.error('Google Maps script failed to load');
      }
    });
  }

  private setMarker(): void {
    if (!this.property?.location) {
      return;
    }

    this.markerPosition = {
      lat: this.property!.location!.lat,
      lng: this.property!.location!.lng,
    };

    this.markerTitle = `${this.property?.neighbourhood?.name}, ${this.property?.city}, ${this.property?.country?.name}`;

    this.googleMapsUrl = `https://www.google.com/maps?q=${this.markerPosition.lat},${this.markerPosition.lng}`;
  }

  private setupBreadcrumb(propertyName: string): void {
    this.breadcrumdItems = [
      {
        label: 'Properties',
        routerLink: '/home/properties',
      },
      {
        label: propertyName || 'Property Details',
      },
    ];
  }

  ngOnDestroy(): void {
    this.propertySub.unsubscribe();
    this.bookmarkedPropertiesSub.unsubscribe();
    this.isAnonymousSessionSub.unsubscribe();

    // Reset property
    this._propertiesService.resetProperty();
  }
}

enum FinancialsTab {
  PROPERTY_COST = 0,
  INCOME_YEAR_1 = 1,
}
